import { supabase } from '../../lib/supabase';

// `rpcUnknown` escape hatch (same pattern as ./api.ts): list_published_tests /
// start-practice aren't in the generated types until the user re-runs
// `supabase gen types`, so we narrow through `unknown` to stay typecheck-clean.
interface UnknownRpc {
  rpc: (
    fn: string,
    args?: Record<string, unknown>,
  ) => Promise<{ data: unknown; error: { message: string } | null }>;
}
const rpcUnknown = supabase as unknown as UnknownRpc;

export type PracticeSectionKind = 'listening' | 'reading' | 'writing' | 'speaking';

export interface PublishedTest {
  id: string;
  title: string;
  exam_type: 'academic' | 'general';
  total_duration_minutes: number;
  section_kinds: PracticeSectionKind[];
  /** True ⇒ a global test open to every student (no scheduling, unlimited attempts). */
  is_global: boolean;
}

export const PRACTICE_QUERY_KEYS = {
  publishedTests: ['student', 'published_tests'] as const,
};

export async function listPublishedTests(): Promise<PublishedTest[]> {
  const { data, error } = await rpcUnknown.rpc('list_published_tests');
  if (error !== null) throw new Error(error.message);
  return (data as PublishedTest[] | null) ?? [];
}

export interface StartPracticeResult {
  ok: boolean;
  resumed?: boolean;
  attempt?: { id: string; status: string };
  error?: string;
}

/**
 * Start — or resume — a self-serve practice attempt against a published test.
 *
 * Phase 159: an unfinished attempt on this test comes back with
 * `resumed: true` rather than being wiped. `{ restart: true }` discards it and
 * starts fresh, and the UI only sends that behind an explicit confirmation:
 * the delete is irreversible (ON DELETE CASCADE takes the answers with it).
 * Completed attempts are never touched, so retakes stay unlimited.
 */
export async function startPractice(
  testId: string,
  options?: { restart?: boolean },
): Promise<StartPracticeResult> {
  const { data, error } = await supabase.functions.invoke<StartPracticeResult>('start-practice', {
    body: { test_id: testId, restart: options?.restart === true },
  });
  if (error !== null) return { ok: false, error: 'request_failed' };
  return data ?? { ok: false, error: 'no_body' };
}
